// ABOUTME: Renders a "soon no longer available" chip on model-selection product cards.
// ABOUTME: Shows when a product's phased_out date is within the configured soonLabelDays window.

import type { IProduct } from '@coolshop/cool-model-selection';
import { Box, Chip } from '@ds/coolshop';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

type ProductWithPhaseDates = IProduct & {
    phasedInDate?: string;
    phasedOutDate?: string;
};

/**
 * Start-action slot for the model-selection product card. Reads the phased_out date carried on
 * the product and renders a warning chip when the model is within `phaseOut.soonLabelDays` of its
 * phase-out date (and not already discontinued — those are filtered out server-side).
 */
export function SoonAvailabilityBadge(product: IProduct) {
    const { formatMessage } = useIntl();
    const settings = useContext(SettingsContext);

    const phaseOut = settings?.prototype?.phaseOut;
    const { phasedOutDate } = product as ProductWithPhaseDates;

    if (!phaseOut?.soonLabelEnabled || !phasedOutDate) {
        return null;
    }

    const phasedOutTime = new Date(phasedOutDate).getTime();
    // An unparseable / malformed date (e.g. a cleared field stored as "NaN-NaN-NaN") must not
    // trigger the badge: NaN comparisons are always false and would otherwise slip through.
    if (Number.isNaN(phasedOutTime)) {
        return null;
    }

    const soonLabelDays = phaseOut.soonLabelDays ?? 0;
    const daysUntilPhaseOut = Math.floor((phasedOutTime - Date.now()) / MS_PER_DAY);

    if (daysUntilPhaseOut < 0 || daysUntilPhaseOut > soonLabelDays) {
        return null;
    }

    return (
        <Box>
            <Chip
                label={formatMessage({
                    id: 'catalog.phase-out.soon-label',
                    defaultMessage: 'Soon no longer available',
                })}
                color="warning"
                size="small"
            />
        </Box>
    );
}
